import { useState } from 'react';
import { aiClient, AiBudgetExhaustedError } from './lib/aiClient.js';

// Sonnet-backed rewrite UI. The editor types a hint (or skips), clicks Rewrite,
// reviews the draft, then Accept (writes back to the article + bumps voice
// profile) or Reject (discards).
export default function RewriteTool({
  articleId,
  isoDate,
  original,
  onAccept,
  onClose,
}) {
  const [hint, setHint] = useState('');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runRewrite = async () => {
    setLoading(true);
    setError(null);
    setDraft('');
    try {
      const data = await aiClient.rewrite({ 
        articleId,
        isoDate,
        original,
        hint: hint.trim() || undefined,
      });
      setDraft(data.draft || '');
    } catch (err) {
      if (err instanceof AiBudgetExhaustedError) {
        setError('AI budget hit — Rewrite disabled until the cap resets.');
      } else {
        setError(err.message || 'Rewrite failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const acceptDraft = () => {
    const before = original || '';
    const after = draft || '';
    const shorter = after.length < before.length;
    const denser = countNumbers(after) > countNumbers(before);
    const frontLoaded = looksFrontLoaded(after) && !looksFrontLoaded(before);
    onAccept?.({ draft, signals: { shorter, denser, frontLoaded } });
  };

  return (
    <div className="bg-hxd-orange-soft border border-hxd-orange/30 rounded-[3px] p-3 mt-3">
      <div className="flex items-center justify-between mb-3">
        <span className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-orange">
          Rewrite · Sonnet 4.6
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-hxd-muted hover:text-hxd-ink"
        >
          Close
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mb-3">
        <input
          className="hxd-input bg-white"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          placeholder="Optional hint: tighten, add a comparable, sharpen the takeaway…"
          disabled={loading}
        />
        <button
          type="button"
          onClick={runRewrite}
          disabled={loading || !original}
          className="hxd-btn hxd-btn-primary hxd-btn-sm"
        >
          {loading ? 'Rewriting…' : draft ? 'Re-roll' : 'Rewrite'}
        </button>
      </div>

      {error && (
        <p className="text-[12px] text-hxd-err mb-3 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </p>
      )}

      {draft && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted mb-1">
                Original
              </div>
              <pre className="whitespace-pre-wrap text-[12px] text-hxd-ink bg-white border border-hxd-border-soft rounded p-2 max-h-64 overflow-auto">
                {original}
              </pre>
            </div>
            <div>
              <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-orange mb-1">
                Draft
              </div>
              <pre className="whitespace-pre-wrap text-[12px] text-hxd-ink bg-white border border-hxd-orange/30 rounded p-2 max-h-64 overflow-auto">
                {draft}
              </pre>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={acceptDraft}
              className="hxd-btn hxd-btn-primary hxd-btn-sm"
            >
              Accept draft
            </button>
            <button
              type="button"
              onClick={() => setDraft('')}
              className="hxd-btn hxd-btn-ghost hxd-btn-sm"
            >
              Reject
            </button>
            <span className="text-[11px] text-hxd-muted ml-auto">
              Edit distance: {editDistance(original, draft)}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function countNumbers(s) {
  if (!s) return 0;
  return (s.match(/\d+(?:[.,]\d+)?/g) || []).length;
}

function looksFrontLoaded(s) {
  if (!s) return false;
  // First sentence ends in a period; "front-loaded" if takeaway-ish words land early.
  const first = s.split(/[.!?]/)[0] || '';
  return /[\d%$]|\b(announced|reported|raised|grounded|cert|FAA)\b/i.test(first);
}

function editDistance(a, b) {
  if (!a || !b) return Math.max((a || '').length, (b || '').length);
  if (a === b) return 0;
  const len = (s) => s.length;
  return Math.abs(len(a) - len(b));
}
